import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import recallsService from '../../../../../services/recalls.service'
import { useForm, Controller } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import Loading from "../../../../common/Loading";

function RecallTimings() {
    const { clinicId } = useParams()
    const [recallTimings, setRecallTimings] = useState([])
    const [editing, setEditing] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        getRecallTimings()
    }, [])

    const getRecallTimings = () => {
        setLoading(true)
        recallsService.getClinicTimings(clinicId)
            .then(res => {
                setRecallTimings(res.data)
                setLoading(false)
            })
            .catch(err => {
                console.log(err)
                toast.error("Failed to load recall timings")
                setLoading(false)
            })
    }

    const handleUpdateTiming = (newTiming) => {
        setRecallTimings(newTiming)
        setEditing(false)
    }

    if (loading) {
        return <Loading />
    }

    return (
        <div className="row">
            <div className="col-12">
                <div className="card card-padding card-border shadow-none">
                    <div className="card-title bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                        <div className=" mb-0 d-flex align-items-center">
                            <i className="bi bi-clock-history text-primary me-2 fs-5"></i>
                            <div className="mb-0">Recall Timings</div>
                        </div>
                        <button
                            className={`btn btn-sm ${editing ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                            onClick={() => setEditing(!editing)}
                        >
                            <i className={`bi ${editing ? 'bi-x-circle' : 'bi-pencil'} me-1`}></i>
                            {editing ? 'Cancel' : 'Edit'}
                        </button>
                    </div>

                    <div className="card-body">
                        {recallTimings && recallTimings.length > 0 ? (
                            editing ?
                                <RecallTimingForm
                                    timings={recallTimings}
                                    updateTiming={handleUpdateTiming}
                                    setEditing={setEditing}
                                /> :
                                <RecallTimingView timings={recallTimings} />
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <i className="bi bi-clock display-4"></i>
                                <p className="mt-3">No recall timings configured</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

const RecallTimingView = ({ timings }) => {
    const StatusBadge = ({ isWorking }) => (
        <span className={`badge service-status small fw-bold ${isWorking ? 'status-completed text-success' : 'status-incomplete text-danger'} px-2 py-1`}>
            {isWorking ? 'Working' : 'Off'}
        </span>
    )

    const formatTime = (timeString) => {
        if (!timeString) return '--:--';
        return timeString.substring(0, 5); // Return only HH:MM part
    }

    return (
        <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                <tr>
                    <th className="ps-4 py-3 col-2">
                        <i className="bi bi-calendar-day me-2"></i>Day
                    </th>
                    <th className="text-center py-3 col-2">Status</th>
                    <th className="py-3 col-4">
                        <i className="bi bi-play-circle me-2"></i>Start Time
                    </th>
                    <th className="pe-4 py-3 col-4">
                        <i className="bi bi-stop-circle me-2"></i>End Time
                    </th>
                </tr>
                </thead>
                <tbody>
                {timings.map((timing, index) => (
                    <tr key={timing.id} className={index % 2 === 0 ? 'bg-light' : ''}>
                        <td className="ps-4 fw-bold">{timing.day}</td>
                        <td className="text-center">
                            <StatusBadge isWorking={timing.isWorking} />
                        </td>
                        <td>
                            {timing.isWorking ? (
                                <span className="badge bg-light text-dark border">
                                        <i className="bi bi-clock me-1"></i>
                                    {formatTime(timing.start)}
                                    </span>
                            ) : (
                                <span className="text-muted">--:--</span>
                            )}
                        </td>
                        <td className="pe-4">
                            {timing.isWorking ? (
                                <span className="badge bg-light text-dark border">
                                        <i className="bi bi-clock me-1"></i>
                                    {formatTime(timing.end)}
                                    </span>
                            ) : (
                                <span className="text-muted">--:--</span>
                            )}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <div className="alert small alert-info mt-3">
                <i className="bi bi-info-circle me-2"></i>
                Recall Timings are based on Clinic's time which may vary with Timezone.
            </div>
        </div>
    )
}

const RecallTimingForm = ({ timings, updateTiming, setEditing }) => {
    const { control, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
        defaultValues: {
            timings: timings.map(timing => ({
                id: timing.id,
                clinicId: timing.clinicId,
                day: timing.day,
                isWorking: timing.isWorking,
                start: timing.start ? timing.start.substring(0, 5) : '10:00', // Extract HH:MM part
                end: timing.end ? timing.end.substring(0, 5) : '16:00' // Extract HH:MM part
            }))
        }
    });

    const [workingStates, setWorkingStates] = useState(
        timings.reduce((acc, timing, index) => {
            acc[index] = timing.isWorking;
            return acc;
        }, {})
    );

    const handleWorkingToggle = (index, isChecked) => {
        setWorkingStates(prev => ({ ...prev, [index]: isChecked }));
        setValue(`timings[${index}].isWorking`, isChecked);

        // Reset times when disabling
        if (!isChecked) {
            setValue(`timings[${index}].start`, '10:00');
            setValue(`timings[${index}].end`, '16:00');
        }
    };

    const onSubmit = async (data) => {
        try {
            const newData = data.timings.map((item) => ({
                id: item.id,
                clinicId: item.clinicId,
                day: item.day,
                isWorking: item.isWorking,
                // Ensure time is stored in HH:MM:SS format
                start: item.isWorking ? `${item.start}:00` : null,
                end: item.isWorking ? `${item.end}:00` : null
            }))

            const response = await recallsService.saveClinicTimings(timings[0].clinicId, newData)
            updateTiming(response.data)

            toast.success("Recall timings updated successfully!")
        } catch (err) {
            toast.error("Error updating recall timings. Please try again.")
            console.log(err)
        }
    };

    const ToggleSwitch = ({ name, checked, onChange, label }) => (
        <div className="form-check form-switch">
            <input
                className="form-check-input"
                type="checkbox"
                id={name}
                checked={checked}
                onChange={onChange}
                style={{ width: '2.5em', height: '1.25em' }}
            />
            <label className="form-check-label fw-semibold ms-2" htmlFor={name}>
                {label}
            </label>
        </div>
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead className="table-light">
                    <tr>
                        <th className="ps-4 py-3">
                            <i className="bi bi-calendar-day me-2"></i>Day
                        </th>
                        <th className="text-center py-3">Working Day</th>
                        <th className="py-3">
                            <i className="bi bi-play-circle me-2"></i>Start Time
                        </th>
                        <th className="pe-4 py-3">
                            <i className="bi bi-stop-circle me-2"></i>End Time
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {timings.map((timing, index) => {
                        const isWorking = workingStates[index];

                        return (
                            <tr key={timing.id} className={index % 2 === 0 ? 'bg-light' : ''}>
                                <td className="ps-4 fw-medium">{timing.day}</td>
                                <td className="text-center">
                                    <ToggleSwitch
                                        name={`isWorking-${timing.day}`}
                                        checked={isWorking}
                                        onChange={(e) => handleWorkingToggle(index, e.target.checked)}
                                        label={isWorking ? "Yes" : "No"}
                                    />
                                </td>
                                <td>
                                    <Controller
                                        name={`timings[${index}].start`}
                                        control={control}
                                        defaultValue={timing.start ? timing.start.substring(0, 5) : '10:00'}
                                        render={({ field }) => (
                                            <input
                                                type="time"
                                                className={`form-control ${isWorking ? '' : 'bg-light'}`}
                                                {...field}
                                                disabled={!isWorking}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                step="300" // 5 minutes interval
                                            />
                                        )}
                                    />
                                </td>
                                <td className="pe-4">
                                    <Controller
                                        name={`timings[${index}].end`}
                                        control={control}
                                        defaultValue={timing.end ? timing.end.substring(0, 5) : '16:00'}
                                        render={({ field }) => (
                                            <input
                                                type="time"
                                                className={`form-control ${isWorking ? '' : 'bg-light'}`}
                                                {...field}
                                                disabled={!isWorking}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                step="300" // 5 minutes interval
                                            />
                                        )}
                                    />
                                </td>
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </div>

            <div className="text-end mt-4 pt-3 border-top">
                <button
                    type="button"
                    className="btn btn-sm btn-danger me-2"
                    onClick={() => setEditing(false)}
                >
                    <i className="bi bi-x"></i> Cancel
                </button>
                <button
                    className='btn btn-success btn-sm'
                    type="submit"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Updating...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-circle me-2"></i>
                            Save Changes
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}

export default RecallTimings